import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AdminLogo from "@/components/AdminLogo";
import { Loader2 } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error(t("auth.password_mismatch", "Passwords do not match")); return; }
    if (pw.length < 8) { toast.error(t("auth.password_short", "Password must be at least 8 characters")); return; }
    setLoading(true);
    try {
      await api.post("/v2/auth/reset-password", { token, new_password: pw });
      toast.success(t("auth.password_reset_success", "Password reset. Please sign in."));
      setTimeout(() => navigate("/admin/login"), 800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("auth.reset_failed", "Reset failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }} />
      <div className="w-full max-w-md p-9 bg-zinc-900/85 backdrop-blur-2xl rounded-[28px] border border-zinc-800/80 shadow-2xl relative">
        <div className="flex items-center gap-3 mb-8">
          <AdminLogo size={44} variant="light" />
          <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-medium">Adelina · Employment Agency</div>
        </div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#d4a017] font-semibold mb-2">
          {t("auth.reset_overline", "Reset Password")}
        </div>
        <h1 className="font-serif text-white text-3xl mb-6 tracking-tight">
          {t("auth.reset_title", "Choose a new password")}
        </h1>
        {!token ? (
          <div className="text-red-300 text-sm">{t("auth.reset_missing_token", "Missing reset token. Please use the link from your email.")}</div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("auth.reset_new", "New password")}</Label>
              <Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" required data-testid="reset-pw" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("auth.reset_confirm", "Confirm password")}</Label>
              <Input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" required data-testid="reset-pw2" />
            </div>
            <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60 flex items-center justify-center gap-2" data-testid="reset-submit">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? t("auth.reset_submitting", "Resetting…") : t("auth.reset_submit", "Reset password")}
            </button>
          </form>
        )}
        <div className="mt-6 text-sm text-zinc-500">
          <Link to="/admin/login" className="hover:text-[#d4a017] transition-colors">
            {t("auth.back_signin", "← Back to sign in")}
          </Link>
        </div>
      </div>
    </div>
  );
}
